/**
 * Create Taxonomy Modal Component
 * 
 * Modal dialog for creating new taxonomies in the knowledge management system.
 */

import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Label } from '@/components/ui';
import { Input as BaseInput } from '@/components/ui/input';
import { Textarea as BaseTextarea } from '@/components/ui/textarea';
import apiClient from '@/lib/api-client';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';

// =============================================================================
// Types
// =============================================================================

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
}

interface FormData {
  name: string;
  description: string;
}

// =============================================================================
// Component
// =============================================================================

export function CreateTaxonomyModal({ open, onOpenChange, tenantId }: Props) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const form = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
    },
  });
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      apiClient.post('/api/v1/taxonomy/nodes', { ...data, parent_id: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxonomies', tenantId] });
      toast.success('Taxonomy created successfully');
      onOpenChange(false);
      form.reset();
    },
    onError: () => {
      toast.error('Failed to create taxonomy');
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    mutation.mutate(data);
  });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { if (form.formState.isDirty) { setConfirmDiscardOpen(true); } else { onOpenChange(false); } } }}>
      <DialogContent
        className="sm:max-w-md"
        dirty={form.formState.isDirty}
        discardTitle={t('common.discardChanges.title') ?? 'Discard changes?'}
        discardMessage={t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.'}
        discardConfirmLabel={t('common.discardChanges.discard') ?? 'Discard'}
        discardCancelLabel={t('common.discardChanges.keepEditing') ?? 'Keep editing'}
      >
        <DialogHeader>
          <DialogTitle>Create Taxonomy</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">{t('common.name')}</Label>
              <BaseInput
                id="name"
                {...form.register('name', { required: 'Name is required' })}
                placeholder="e.g., Technology, Document Type"
                className="mt-1"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="description">{t('common.description')}</Label>
              <BaseTextarea
                id="description"
                {...form.register('description')}
                placeholder="Optional description"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { if (form.formState.isDirty) { setConfirmDiscardOpen(true); } else { onOpenChange(false); } }}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? t('common.loading') : t('common.create')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={t('common.discardChanges.title') ?? 'Discard changes?'}
        message={t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.'}
        confirmLabel={t('common.discardChanges.discard') ?? 'Discard'}
        cancelLabel={t('common.discardChanges.keepEditing') ?? 'Keep editing'}
      />
    </Dialog>
  );
}
